// Auth-session helpers.
//
// AIRIS derives the signed-in user purely from the OIDC `id_token` issued by the
// authorization server. To avoid showing a stale user after someone logs out /
// logs in as a different person on the auth server (shared machine), AIRIS no
// longer trusts a locally-cached token on a deep-link entry:
//
//   * we never restore identity from a localStorage mirror, and
//   * every full page load on a real route is bounced through the OAuth
//     `authorize` flow (see ensureFreshSession + components/Redirect.js), which
//     round-trips the auth server's SSO session and returns a token for the
//     *current* user (or bounces to the login page if nobody is signed in).
//
// clearAuth() still wipes any legacy mirror written by older builds.
export const AUTH_KEYS = ["id_token", "userInfo"];

// One-shot stash for "where the user actually wanted to go" across the OAuth
// round-trip.
const POST_LOGIN_KEY = "postLoginRedirect";

// Decode a JWT payload without a third-party library. Returns null on any error.
const decodePayload = (token) => {
  try {
    const part = token.split(".")[1];
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch (e) {
    return null;
  }
};

// True only when the token decodes and its `exp` (seconds) is still in the
// future (10s clock-skew allowance).
export const isTokenValid = (token) => {
  if (!token) return false;
  const payload = decodePayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return payload.exp * 1000 > Date.now() + 10_000;
};

// Kept for backwards compatibility with older imports (components/Redirect.js).
// The localStorage mirror is no longer trusted for authentication, so this is a
// deliberate no-op.
export const persistAuth = () => {};

// Startup sanity check: if this tab holds an id_token that is expired or
// unparseable, drop it so we fall through to a fresh OAuth flow instead of
// rendering the app from a dead token.
export const hydrateAuth = () => {
  const token = sessionStorage.getItem("id_token");
  if (token && !isTokenValid(token)) {
    clearAuth();
    sessionStorage.clear();
  }
};

// Drop every remembered auth key from both stores (used on logout and whenever
// a stale/expired token is detected).
export const clearAuth = () => {
  AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

// Called once on startup, before React mounts.
//
// Unless we are already at the app root or handling the OAuth callback, send the
// browser to the app root so components/Redirect.js re-runs the OAuth
// `authorize` step. With an active SSO session this is a silent round-trip that
// yields a token for the current user; with no session it lands on the auth
// server's login page. Either way the app is never rendered from a cached token
// on a deep-link entry.
//
// Returns true when a redirect was triggered (caller should skip render).
export const ensureFreshSession = (appUrl) => {
  const base = new URL(appUrl).pathname.replace(/\/$/, ""); // e.g. "/ptw"
  const path = window.location.pathname.replace(/\/$/, "");

  // Already at the app root or in the OAuth callback - let React / Redirect.js
  // drive the flow.
  if (path === base || window.location.pathname.includes("/authorized")) {
    return false;
  }

  // Remember the intended destination (relative to the router basename) so we
  // can return there once the OAuth round-trip completes.
  const target =
    (path.startsWith(base) ? path.slice(base.length) : path) +
    window.location.search;
  if (target && target !== "/") {
    localStorage.setItem(POST_LOGIN_KEY, target);
  }

  window.location.replace(appUrl);
  return true;
};

// Consume the stashed post-login destination (one-shot). Falls back to null.
export const takePostLoginTarget = () => {
  const target = localStorage.getItem(POST_LOGIN_KEY);
  localStorage.removeItem(POST_LOGIN_KEY);
  return target || null;
};
